import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { BarChart3, Users, QrCode, TrendingUp, Gift, Download, Filter, Loader2, PieChart, Activity, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RPieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";

const COLORS = ["#008A99", "#32BFAD", "#F34872", "#FEE24D", "#94A3B8", "#0EA5E9", "#10B981", "#F59E0B"];

const TIER_METAL_STYLE = {
  Silver:   { background: "linear-gradient(145deg, #F4F7FA 0%, #B6C2D0 55%, #6F7A8A 100%)", color: "#1A2530", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.15)" },
  Gold:     { background: "linear-gradient(145deg, #FBE38B 0%, #D4A93A 55%, #8C6618 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.15)" },
  Platinum: { background: "linear-gradient(145deg, #2B50B0 0%, #173075 55%, #0A1736 100%)", color: "#FFFFFF", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.4), 0 1px 2px rgba(0,0,0,0.2)" },
};

function StatCard({ label, value, icon: Icon, color, bg }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm" data-testid={`report-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-9 h-9 ${bg} rounded-xl flex items-center justify-center mb-2`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium mt-0.5">{label}</p>
    </div>
  );
}

function SegmentBar({ segments, colorMap }) {
  const total = segments.reduce((s, seg) => s + seg.count, 0);
  if (!total) return <div className="text-sm text-slate-400">No data</div>;
  return (
    <div className="space-y-2">
      <div className="flex h-6 rounded-full overflow-hidden bg-slate-100">
        {segments.map((seg, i) => (
          <div key={i} style={{ width: `${(seg.count / total) * 100}%`, backgroundColor: COLORS[i % COLORS.length] }} className="transition-all" title={`${seg.segment}: ${seg.count}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
            <span className="text-slate-600">{seg.segment}</span>
            <span className="font-semibold text-[#0F172A]">{seg.count}</span>
            {seg.percent !== undefined && <span className="text-slate-400">({seg.percent}%)</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Overview Tab ───
function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => { (async () => { try { const { data: d } = await API.get("/admin/reports/overview"); setData(d); } catch {} finally { setLoading(false); } })(); }, []);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>;
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard label="Total Users" value={data.total_users} icon={Users} color="text-[#008A99]" bg="bg-[#E0F4F5]" />
        <StatCard label="Total Scans" value={data.total_scans} icon={QrCode} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="Points Earned" value={data.total_points_earned} icon={TrendingUp} color="text-amber-600" bg="bg-amber-50" />
        <StatCard label="Points Redeemed" value={data.total_points_burned} icon={Gift} color="text-red-500" bg="bg-red-50" />
        <StatCard label="Avg Pts/User" value={data.avg_points_per_user} icon={BarChart3} color="text-sky-600" bg="bg-sky-50" />
        <StatCard label="Avg Scans/User" value={data.avg_scans_per_user} icon={Activity} color="text-[#008A99]" bg="bg-[#E0F4F5]" />
        <StatCard label="KYC Done" value={data.kyc_done} icon={Users} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="KYC Pending" value={data.kyc_pending} icon={Users} color="text-amber-600" bg="bg-amber-50" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Tier Distribution</h4>
          {Object.keys(data.tier_distribution).length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <RPieChart>
                <Pie data={Object.entries(data.tier_distribution).map(([k, v]) => ({ name: k, value: v }))} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {Object.keys(data.tier_distribution).map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
                </Pie>
                <Tooltip />
              </RPieChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm py-8 text-center">No data</p>}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Key Metrics</h4>
          <div className="space-y-3">
            {[
              { label: "Earn Transactions", value: data.earn_transactions },
              { label: "Burn Transactions", value: data.burn_transactions },
              { label: "Total Products", value: data.total_products },
              { label: "Total QR Codes", value: data.total_qr_codes },
              { label: "Total Redemptions", value: data.total_redemptions },
            ].map((m) => (
              <div key={m.label} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-500">{m.label}</span>
                <span className="text-sm font-bold text-[#0F172A]">{(m.value || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Registrations Tab ───
function RegistrationsTab() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState("day");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await API.get(`/admin/reports/registrations?days=${days}&group_by=${groupBy}`); setData(d.data || []); setTotal(d.total || 0); } catch {} finally { setLoading(false); }
  }, [days, groupBy]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="registrations-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="reg-days-filter">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={180}>Last 6 months</option><option value={365}>Last year</option>
        </select>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="reg-group-filter">
          <option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto">Total: <b>{total}</b> registrations</span>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div> : (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          {data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="count" fill="#008A99" radius={[4, 4, 0, 0]} name="Registrations" /></BarChart>
            </ResponsiveContainer>
          ) : <p className="text-slate-400 text-sm py-12 text-center">No registrations in this period</p>}
        </div>
      )}
    </div>
  );
}

// ─── Scans Tab ───
function ScansTab() {
  const [data, setData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [days, setDays] = useState(30);
  const [groupBy, setGroupBy] = useState("day");
  const [reagentType, setReagentType] = useState("");
  const [loading, setLoading] = useState(true);
  const [totalScans, setTotalScans] = useState(0);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, group_by: groupBy });
      if (reagentType) params.set("reagent_type", reagentType);
      const { data: d } = await API.get(`/admin/reports/scans?${params}`);
      setData(d.data || []); setTopProducts(d.top_products || []); setTotalScans(d.total_scans || 0);
    } catch {} finally { setLoading(false); }
  }, [days, groupBy, reagentType]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-4" data-testid="scans-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
        </select>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white">
          <option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option>
        </select>
        <select value={reagentType} onChange={(e) => setReagentType(e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white">
          <option value="">All Types</option><option value="open">Open</option><option value="closed">Closed</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto">Total: <b>{totalScans}</b> scans</span>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h4 className="font-semibold text-[#0F172A] mb-3 text-sm" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Scans & Points Over Time</h4>
            {data.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis yAxisId="left" tick={{ fontSize: 10 }} /><YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} /><Tooltip /><Legend /><Line yAxisId="left" type="monotone" dataKey="count" stroke="#008A99" strokeWidth={2} name="Scans" dot={false} /><Line yAxisId="right" type="monotone" dataKey="points" stroke="#32BFAD" strokeWidth={2} name="Points" dot={false} /></LineChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-400 text-sm py-12 text-center">No scans in this period</p>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h4 className="font-semibold text-[#0F172A] mb-3 text-sm" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Top Scanned Products</h4>
            <div className="space-y-2">
              {topProducts.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div><p className="text-xs font-medium text-[#0F172A]">{p.name}</p><p className="text-[10px] text-slate-400">{p.product_code}</p></div>
                  <div className="text-right"><p className="text-xs font-bold text-[#008A99]">{p.count} scans</p><p className="text-[10px] text-slate-400">{p.points} pts</p></div>
                </div>
              ))}
              {topProducts.length === 0 && <p className="text-slate-400 text-xs text-center py-4">No data</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Points Tab ───
function PointsTab() {
  const [earnData, setEarnData] = useState([]);
  const [burnData, setBurnData] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await API.get(`/admin/reports/points-summary?days=${days}`); setEarnData(d.earn_data || []); setBurnData(d.burn_data || []); setSourceBreakdown(d.source_breakdown || []); } catch {} finally { setLoading(false); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  // Merge earn/burn into single chart
  const merged = {};
  earnData.forEach(d => { merged[d.date] = { ...merged[d.date], date: d.date, earned: d.points }; });
  burnData.forEach(d => { merged[d.date] = { ...merged[d.date], date: d.date, burned: d.points }; });
  const chartData = Object.values(merged).sort((a, b) => a.date.localeCompare(b.date));
  return (
    <div className="space-y-4" data-testid="points-tab">
      <div className="flex items-center gap-3">
        <select value={days} onChange={(e) => setDays(+e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white">
          <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option><option value={90}>Last 90 days</option><option value={365}>Last year</option>
        </select>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h4 className="font-semibold text-[#0F172A] mb-3 text-sm" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Earn vs Burn</h4>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tick={{ fontSize: 10 }} /><YAxis tick={{ fontSize: 10 }} /><Tooltip /><Legend /><Bar dataKey="earned" fill="#10B981" name="Earned" radius={[4, 4, 0, 0]} /><Bar dataKey="burned" fill="#EF4444" name="Burned" radius={[4, 4, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            ) : <p className="text-slate-400 text-sm py-12 text-center">No data</p>}
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <h4 className="font-semibold text-[#0F172A] mb-3 text-sm" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Points by Source</h4>
            <div className="space-y-2">
              {sourceBreakdown.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.type === "earn" ? "#10B981" : "#EF4444" }} />
                    <span className="text-xs text-slate-600 capitalize">{s.source?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-right"><span className="text-xs font-bold">{(s.total || 0).toLocaleString()} pts</span><span className="text-[10px] text-slate-400 ml-1">({s.count}x)</span></div>
                </div>
              ))}
              {sourceBreakdown.length === 0 && <p className="text-slate-400 text-xs text-center py-4">No data</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Customer Data Tab ───
function CustomerDataTab() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ tier: "", city: "", state: "", kyc_status: "", min_points: "", max_points: "", min_scans: "", sort_by: "created_at", sort_order: "desc" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30, sort_by: filters.sort_by, sort_order: filters.sort_order });
      if (filters.tier) params.set("tier", filters.tier);
      if (filters.city) params.set("city", filters.city);
      if (filters.state) params.set("state", filters.state);
      if (filters.kyc_status) params.set("kyc_status", filters.kyc_status);
      if (filters.min_points) params.set("min_points", filters.min_points);
      if (filters.max_points) params.set("max_points", filters.max_points);
      if (filters.min_scans) params.set("min_scans", filters.min_scans);
      const { data: d } = await API.get(`/admin/reports/customers?${params}`);
      setUsers(d.users || []); setTotal(d.total || 0); setTotalPages(d.pages || 1);
      setCities(d.available_cities || []); setStates(d.available_states || []);
    } catch {} finally { setLoading(false); }
  }, [page, filters]);
  useEffect(() => { load(); }, [load]);

  const updateFilter = (key, value) => { setFilters(f => ({ ...f, [key]: value })); setPage(1); };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500"><b>{total}</b> customers match filters</span>
        <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className="rounded-lg text-xs" data-testid="toggle-filters-button">
          <Filter className="w-3.5 h-3.5 mr-1" /> Filters {showFilters ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
        </Button>
      </div>
      {showFilters && (
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Tier</label>
            <select value={filters.tier} onChange={(e) => updateFilter("tier", e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="filter-tier">
              <option value="">All</option><option value="Silver">Silver</option><option value="Gold">Gold</option><option value="Platinum">Platinum</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">KYC</label>
            <select value={filters.kyc_status} onChange={(e) => updateFilter("kyc_status", e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="filter-kyc">
              <option value="">All</option><option value="done">Done</option><option value="pending">Pending</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">City</label>
            <select value={filters.city} onChange={(e) => updateFilter("city", e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="filter-city">
              <option value="">All</option>{cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">State</label>
            <select value={filters.state} onChange={(e) => updateFilter("state", e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="filter-state">
              <option value="">All</option>{states.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Min Points</label>
            <Input type="number" value={filters.min_points} onChange={(e) => updateFilter("min_points", e.target.value)} className="h-8 rounded-lg text-xs" placeholder="0" data-testid="filter-min-points" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Max Points</label>
            <Input type="number" value={filters.max_points} onChange={(e) => updateFilter("max_points", e.target.value)} className="h-8 rounded-lg text-xs" placeholder="99999" data-testid="filter-max-points" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Min Scans</label>
            <Input type="number" value={filters.min_scans} onChange={(e) => updateFilter("min_scans", e.target.value)} className="h-8 rounded-lg text-xs" placeholder="0" data-testid="filter-min-scans" />
          </div>
          <div>
            <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Sort By</label>
            <select value={filters.sort_by} onChange={(e) => updateFilter("sort_by", e.target.value)} className="w-full h-8 rounded-lg border border-slate-200 px-2 text-xs bg-white" data-testid="filter-sort">
              <option value="created_at">Registration Date</option><option value="balance_points">Points Balance</option><option value="total_scans">Total Scans</option><option value="total_points_earned">Total Earned</option>
            </select>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">Name</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">Mobile</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">Lab</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">City</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">State</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">Tier</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-700">Balance</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-700">Earned</th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-700">Scans</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">KYC</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-700">Registered</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`cust-row-${u.id}`}>
                    <td className="px-3 py-2 font-medium text-[#0F172A]">{u.full_name || "-"}</td>
                    <td className="px-3 py-2 text-slate-600">{u.mobile}</td>
                    <td className="px-3 py-2 text-slate-500">{u.lab_name || "-"}</td>
                    <td className="px-3 py-2 text-slate-500">{u.city || "-"}</td>
                    <td className="px-3 py-2 text-slate-500">{u.state || "-"}</td>
                    <td className="px-3 py-2"><span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider inline-block" style={TIER_METAL_STYLE[u.tier] || TIER_METAL_STYLE.Silver}>{u.tier || "Silver"}</span></td>
                    <td className="px-3 py-2 text-right font-semibold text-[#008A99]">{(u.balance_points || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{(u.total_points_earned || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{u.total_scans || 0}</td>
                    <td className="px-3 py-2">{u.kyc_completed ? <span className="text-emerald-600">Done</span> : <span className="text-amber-500">Pending</span>}</td>
                    <td className="px-3 py-2 text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "-"}</td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={11} className="px-3 py-12 text-center text-slate-400">No customers found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-xs rounded-lg border disabled:opacity-40">Prev</button>
          <span className="px-3 py-1.5 text-xs">{page} / {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-xs rounded-lg border disabled:opacity-40">Next</button>
        </div>
      )}
    </div>
  );
}

// ─── Cohorts Tab ───
function CohortsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(6);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { data: d } = await API.get(`/admin/reports/cohorts?months=${months}`); setData(d); } catch {} finally { setLoading(false); }
  }, [months]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>;
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="cohorts-tab">
      <div className="flex items-center gap-3">
        <select value={months} onChange={(e) => setMonths(+e.target.value)} className="h-9 rounded-lg border border-slate-200 px-3 text-sm bg-white" data-testid="cohort-months-filter">
          <option value={3}>Last 3 months</option><option value={6}>Last 6 months</option><option value={12}>Last 12 months</option>
        </select>
        <span className="text-sm text-slate-500 ml-auto">Total: <b>{data.total_customers}</b> customers</span>
      </div>

      {/* Activity Segments */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Activity Segments</h4>
        <SegmentBar segments={data.activity_segments || []} />
      </div>

      {/* Scan Frequency */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Scan Frequency Segments</h4>
          <SegmentBar segments={data.scan_frequency_segments || []} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Points Balance Segments</h4>
          <SegmentBar segments={data.points_segments || []} />
        </div>
      </div>

      {/* Registration Cohorts */}
      {(data.registration_cohorts || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h4 className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Registration Cohorts</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-slate-700">Cohort</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Users</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Avg Points</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Avg Scans</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-700">Total Points</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-slate-700">KYC Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {data.registration_cohorts.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-50/50" data-testid={`cohort-row-${i}`}>
                    <td className="px-4 py-2.5 font-medium text-[#0F172A]">{c.cohort}</td>
                    <td className="px-4 py-2.5 text-right font-semibold">{c.users}</td>
                    <td className="px-4 py-2.5 text-right">{c.avg_points}</td>
                    <td className="px-4 py-2.5 text-right">{c.avg_scans}</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-[#008A99]">{c.total_points.toLocaleString()}</td>
                    <td className="px-4 py-2.5 text-right"><span className={`${c.kyc_rate > 50 ? "text-emerald-600" : "text-amber-500"}`}>{c.kyc_rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tier Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Tier Breakdown</h4>
          <div className="space-y-3">
            {Object.entries(data.tier_breakdown || {}).map(([tier, info], i) => (
              <div key={tier} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i] }} />
                  <span className="text-sm font-medium">{tier}</span>
                </div>
                <div className="text-right text-xs">
                  <span className="font-bold">{info.count} users</span>
                  <span className="text-slate-400 ml-2">avg {info.avg_points} pts</span>
                  <span className="text-slate-400 ml-2">{info.avg_scans} scans</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-4" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Geographic Segments</h4>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data.geographic_segments || []).map((g, i) => (
              <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-2"><MapPin className="w-3 h-3 text-slate-400" /><span className="text-xs">{g.state}</span></div>
                <div className="text-right text-xs"><span className="font-bold">{g.count}</span> users <span className="text-slate-400 ml-1">{g.total_points.toLocaleString()} pts</span></div>
              </div>
            ))}
            {(data.geographic_segments || []).length === 0 && <p className="text-slate-400 text-xs text-center py-4">No geographic data</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── DIY Drill-Down Tab ───
function DrillDownTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const { data: d } = await API.get(`/admin/reports/full-drilldown?${params}`);
      setData(d);
    } catch {} finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const downloadUsersCsv = async () => {
    try {
      const resp = await API.get("/admin/reports/export-users", { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "myerba-users.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {}
  };

  const presetRange = (preset) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (preset === "today") { setFrom(fmt(today)); setTo(fmt(today)); }
    else if (preset === "7d") { const d = new Date(today.getTime() - 7*86400000); setFrom(fmt(d)); setTo(fmt(today)); }
    else if (preset === "30d") { const d = new Date(today.getTime() - 30*86400000); setFrom(fmt(d)); setTo(fmt(today)); }
    else { setFrom(""); setTo(""); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>;
  if (!data) return <p className="text-slate-400 text-sm py-12 text-center">No data</p>;

  return (
    <div className="space-y-6" data-testid="drilldown-tab">
      {/* Filters */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm flex flex-wrap items-end gap-3" data-testid="drilldown-filters">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { k: "all", l: "All" }, { k: "today", l: "Today" }, { k: "7d", l: "Last 7d" }, { k: "30d", l: "Last 30d" },
          ].map(p => (
            <button key={p.k} onClick={() => presetRange(p.k)}
              className="px-3 py-1.5 text-xs font-semibold border border-slate-200 rounded-lg hover:border-[#008A99] hover:text-[#008A99]"
              data-testid={`preset-${p.k}`}>{p.l}</button>
          ))}
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="block h-9 px-2 border border-slate-200 rounded-lg text-xs" data-testid="drilldown-from" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className="block h-9 px-2 border border-slate-200 rounded-lg text-xs" data-testid="drilldown-to" />
          </div>
        </div>
        <button onClick={downloadUsersCsv} className="ml-auto px-4 py-2 text-xs font-semibold bg-[#008A99] text-white rounded-lg flex items-center gap-1.5" data-testid="export-users-csv">
          <Download className="w-3.5 h-3.5" /> Export Users CSV
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Users" value={data.kpis.total_users} icon={Users} color="text-[#008A99]" bg="bg-[#E0F4F5]" />
        <StatCard label="Approved" value={data.kpis.kyc_approved} icon={Users} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="KYC Pending" value={data.kpis.kyc_pending} icon={Users} color="text-amber-600" bg="bg-amber-50" />
        <StatCard label="KYC Rejected" value={data.kpis.kyc_rejected} icon={Users} color="text-red-500" bg="bg-red-50" />
        <StatCard label="Scans" value={data.kpis.total_scans} icon={QrCode} color="text-emerald-600" bg="bg-emerald-50" />
        <StatCard label="Redemptions" value={data.kpis.total_redemptions} icon={Gift} color="text-red-500" bg="bg-red-50" />
        <StatCard label="Points Earned" value={data.kpis.points_earned} icon={TrendingUp} color="text-amber-600" bg="bg-amber-50" />
        <StatCard label="Outstanding Pts" value={data.kpis.outstanding_points} icon={Activity} color="text-sky-600" bg="bg-sky-50" />
      </div>

      {/* Funnel */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h4 className="font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Engagement Funnel</h4>
        <div className="space-y-2">
          {data.funnel.map((f, i) => (
            <div key={i} className="flex items-center gap-3" data-testid={`funnel-${f.stage}`}>
              <div className="w-32 text-xs font-semibold text-slate-700">{f.stage}</div>
              <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden relative">
                <div className="h-full bg-gradient-to-r from-[#008A99] to-[#00C2D6] transition-all duration-700" style={{ width: `${f.rate}%` }} />
                <span className="absolute inset-0 flex items-center px-3 text-xs font-bold text-white drop-shadow">{f.count.toLocaleString()} ({f.rate}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Segments */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h4 className="font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Catalogue by Segment</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs">Segment</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Reagents</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Avg Price</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Min</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Max</th>
              </tr>
            </thead>
            <tbody>
              {data.segments.map((s, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0" data-testid={`seg-row-${s.segment}`}>
                  <td className="px-3 py-2 font-medium">{s.segment}</td>
                  <td className="px-3 py-2 text-right">{s.reagents.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-slate-600">₹{s.avg_price.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-slate-500">₹{s.min_price.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-slate-500">₹{s.max_price.toLocaleString()}</td>
                </tr>
              ))}
              {data.segments.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-xs">No catalogue data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top redeemed reagents + Top users */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Top Redeemed Reagents</h4>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {data.top_redeemed.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No redemptions in window</p>}
            {data.top_redeemed.map((r, i) => (
              <div key={i} className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{r.name || r.product_code}</p>
                  <p className="text-[10px] text-slate-400 font-mono">{r.product_code}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-[#008A99]">{r.count}×</p>
                  <p className="text-[10px] text-slate-400">{r.points_redeemed.toLocaleString()} pts</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h4 className="font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Top Customers</h4>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {data.top_users.map((u, i) => (
              <div key={i} className="flex justify-between items-center text-xs py-1.5 border-b border-slate-50 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{u.full_name || u.mobile}</p>
                  <p className="text-[10px] text-slate-400">{[u.city, u.state].filter(Boolean).join(", ") || u.mobile}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-emerald-600">{(u.total_points_earned || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-slate-400">{u.total_scans || 0} scans</p>
                </div>
              </div>
            ))}
            {data.top_users.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No data</p>}
          </div>
        </div>
      </div>

      {/* Region breakdown */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <h4 className="font-semibold text-[#0F172A] mb-3" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Region Breakdown</h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-left">
              <tr>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs">State</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Users</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Scans</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Earned</th>
                <th className="px-3 py-2 font-semibold text-slate-700 text-xs text-right">Burned</th>
              </tr>
            </thead>
            <tbody>
              {data.region.map((r, i) => (
                <tr key={i} className="border-b border-slate-50 last:border-0">
                  <td className="px-3 py-2 font-medium">{r.state}</td>
                  <td className="px-3 py-2 text-right">{r.users.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{r.scans.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-emerald-600 font-semibold">{r.points_earned.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-red-500 font-semibold">{r.points_burned.toLocaleString()}</td>
                </tr>
              ))}
              {data.region.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400 text-xs">No region data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Main Reports Page ───
export default function AdminReports() {
  return (
    <div className="space-y-6 animate-slide-up" data-testid="admin-reports">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Reports & Analytics</h2>
        <p className="text-sm text-gray-500 mt-0.5">Deep insights into loyalty program performance</p>
      </div>
      <Tabs defaultValue="drilldown" className="w-full">
        <TabsList className="w-full bg-white border border-slate-200 rounded-xl p-1 flex flex-wrap gap-1">
          <TabsTrigger value="drilldown" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-drilldown">DIY Drill-Down</TabsTrigger>
          <TabsTrigger value="overview" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="registrations" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-registrations">Registrations</TabsTrigger>
          <TabsTrigger value="scans" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-scans">Scans</TabsTrigger>
          <TabsTrigger value="points" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-points">Points</TabsTrigger>
          <TabsTrigger value="customers" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-customers">Customers</TabsTrigger>
          <TabsTrigger value="cohorts" className="flex-1 min-w-[100px] rounded-lg text-xs data-[state=active]:bg-[#008A99] data-[state=active]:text-white" data-testid="tab-cohorts">Cohorts</TabsTrigger>
        </TabsList>
        <TabsContent value="drilldown"><DrillDownTab /></TabsContent>
        <TabsContent value="overview"><OverviewTab /></TabsContent>
        <TabsContent value="registrations"><RegistrationsTab /></TabsContent>
        <TabsContent value="scans"><ScansTab /></TabsContent>
        <TabsContent value="points"><PointsTab /></TabsContent>
        <TabsContent value="customers"><CustomerDataTab /></TabsContent>
        <TabsContent value="cohorts"><CohortsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
